import React from 'react';
import { Box, Button, CircularProgress, IconButton, Typography, useTheme } from '@mui/material';
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { RISK_COMPLIANCE_BLOCK_STATUS } from '../../dataHandlers/riskComplianceConstants';
import {
  getRiskCardTitleSx,
  getRiskFieldLabelProps,
  getRiskFieldValueProps,
  riskButtonSx,
  riskCardBodySx,
  riskCardHeaderSx,
  riskCardSx,
} from './riskComplianceStyles';

export default function DnBDataCard({
  title,
  icon,
  children,
  blockStatus = RISK_COMPLIANCE_BLOCK_STATUS.LOADED,
  emptyMessage,
  unavailableMessage,
  noDataMessage,
  fetchLabel,
  onFetch,
  onRefresh,
  isFetching = false,
}) {
  const theme = useTheme();
  const isEmpty = blockStatus === RISK_COMPLIANCE_BLOCK_STATUS.EMPTY;
  const isUnavailable = blockStatus === RISK_COMPLIANCE_BLOCK_STATUS.UNAVAILABLE;
  const isNoData = blockStatus === RISK_COMPLIANCE_BLOCK_STATUS.NO_DATA;
  const isLoading = blockStatus === RISK_COMPLIANCE_BLOCK_STATUS.FETCHING || isFetching;
  const canRefresh = !isEmpty && onRefresh;
  const placeholderMessage = isUnavailable ? unavailableMessage : isNoData ? noDataMessage : emptyMessage;
  const showPlaceholder = isEmpty || isUnavailable || isNoData;

  return (
    <Box sx={riskCardSx}>
      <Box sx={riskCardHeaderSx}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
          {icon}
          <Typography variant="subtitle1" sx={getRiskCardTitleSx(theme)} noWrap>
            {title}
          </Typography>
        </Box>
        {canRefresh && (
          <IconButton size="small" onClick={onRefresh} disabled={isLoading} aria-label="Refresh block">
            {isLoading ? <CircularProgress size={18} /> : <RefreshOutlinedIcon fontSize="small" color="action" />}
          </IconButton>
        )}
      </Box>

      <Box
        sx={{
          ...riskCardBodySx,
          ...(showPlaceholder && {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
          }),
        }}
      >
        {showPlaceholder ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, py: 2 }}>
            {(isUnavailable || isNoData) && <InfoOutlinedIcon color="action" sx={{ fontSize: 28 }} />}
            <Typography variant="body2" sx={{ color: theme.palette.text.secondary, maxWidth: 320 }}>
              {placeholderMessage}
            </Typography>
            {isEmpty && onFetch && (
              <Button
                variant="contained"
                startIcon={isLoading ? <CircularProgress size={16} color="inherit" /> : <DownloadOutlinedIcon />}
                onClick={onFetch}
                disabled={isLoading}
                sx={riskButtonSx}
              >
                {fetchLabel}
              </Button>
            )}
          </Box>
        ) : (
          children
        )}
      </Box>
    </Box>
  );
}

export function DnBFieldRow({ label, value }) {
  const theme = useTheme();

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: 2,
        py: 1.25,
        borderBottom: '1px solid',
        borderColor: 'divider',
        '&:last-child': { borderBottom: 'none', pb: 0 },
        '&:first-of-type': { pt: 0 },
      }}
    >
      <Typography {...getRiskFieldLabelProps(theme)}>{label}</Typography>
      <Typography {...getRiskFieldValueProps(theme)}>{value ?? '—'}</Typography>
    </Box>
  );
}
